import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { BarChart3, Boxes, Users } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { ChartConfig, ChartContainer } from "@/components/ui/chart";
import { NumberKpiCard } from "@/components/shared/NumberKpiCard";
import { squadQueries } from "@/services/api/queries/squad.queries";

const CHART_CONFIG = {
  totalMembros: { label: "Membros", color: "hsl(var(--primary))" },
} satisfies ChartConfig;

const TOP_N = 8;
const NOME_MAX_LENGTH = 22;
const BAR_ROW_HEIGHT = 44;

function truncarNome(nome: string) {
  return nome.length > NOME_MAX_LENGTH ? `${nome.slice(0, NOME_MAX_LENGTH - 1)}…` : nome;
}

interface SquadBarDatum {
  id: number;
  nome: string;
  nomeCurto: string;
  totalMembros: number;
  membros: string[];
}

function SquadBarTooltip({ active, payload }: Readonly<TooltipProps<number, string>>) {
  const item = payload?.[0];
  if (!active || !item) return null;

  const squad = item.payload as SquadBarDatum;

  return (
    <div className="max-w-64 rounded-lg border border-border bg-popover px-3 py-2 text-sm shadow-md">
      <p className="font-medium text-foreground">{squad.nome}</p>
      <p className="mb-1.5 text-muted-foreground">
        {squad.totalMembros} {squad.totalMembros === 1 ? "membro" : "membros"}
      </p>
      {squad.membros.length > 0 && (
        <ul className="max-h-40 space-y-0.5 overflow-y-auto border-t border-border/60 pt-1.5">
          {squad.membros.map((membro) => (
            <li key={membro} className="text-xs text-muted-foreground">
              {membro}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SquadEstatisticasPanel() {
  const { data, isLoading } = useQuery(squadQueries.estatisticas());

  const topSquads = useMemo(
    () =>
      (data?.ranking ?? [])
        .slice(0, TOP_N)
        .map((squad) => ({ ...squad, nomeCurto: truncarNome(squad.nome) })),
    [data],
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
        <Skeleton className="h-80 w-full rounded-xl" />
      </div>
    );
  }

  if (!data || data.totalSquads === 0) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border/60 text-center">
        <Boxes className="h-8 w-8 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">
          Nenhuma squad ativa para exibir estatísticas.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <NumberKpiCard title="Squads ativas" value={data.totalSquads} icon={Boxes} variant="blue" />
        <NumberKpiCard
          title="Membros no total"
          value={data.totalMembros}
          icon={Users}
          variant="emerald"
        />
        <NumberKpiCard
          title="Média de membros por squad"
          value={data.mediaMembrosPorSquad.toFixed(1)}
          icon={BarChart3}
          variant="purple"
        />
      </div>

      <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-semibold text-foreground">
            Squads com mais membros{data.ranking.length > TOP_N ? ` (top ${TOP_N})` : ""}
          </p>
        </div>

        <ChartContainer
          config={CHART_CONFIG}
          className="!aspect-auto w-full"
          style={{ height: Math.max(topSquads.length * BAR_ROW_HEIGHT, 120) }}
        >
          <BarChart
            data={topSquads}
            layout="vertical"
            margin={{ left: 0, right: 28, top: 4, bottom: 4 }}
          >
            <defs>
              <linearGradient id="fillMembros" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.85} />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.55} />
              </linearGradient>
            </defs>
            <CartesianGrid horizontal={false} strokeDasharray="3 3" className="stroke-border/50" />
            <XAxis
              type="number"
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              className="text-[11px]"
            />
            <YAxis
              type="category"
              dataKey="nomeCurto"
              tickLine={false}
              axisLine={false}
              width={140}
              className="text-[11px]"
            />
            <Tooltip
              cursor={{ fill: "hsl(var(--muted))" }}
              content={<SquadBarTooltip />}
              wrapperStyle={{ zIndex: 50, outline: "none" }}
            />
            <Bar
              dataKey="totalMembros"
              fill="url(#fillMembros)"
              radius={[0, 6, 6, 0]}
              maxBarSize={28}
            >
              <LabelList
                dataKey="totalMembros"
                position="right"
                className="fill-foreground text-[11px] font-medium"
              />
            </Bar>
          </BarChart>
        </ChartContainer>
      </div>
    </div>
  );
}
